import './bet-panel.scss';

import NiceModal from '@ebay/nice-modal-react';
import { Pencil, Zap } from 'lucide-react';
import { memo } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import { selectQuickBuyPresets } from '@/entities/preferences';
import { MODALS } from '@/shared/config/modals';
import { type CurrencyCode, formatCurrencyAmount } from '@/shared/lib/currency';
import { AssetTabs } from '@/shared/ui/AssetTabs';
import { CURRENCY_TABS } from '@/shared/ui/CurrencyTabs/currencyTabs.config';

export interface BetPanelProps {
  currency: CurrencyCode;
  onCurrencyChange: (currency: CurrencyCode) => void;
  onSelectAmount: (amount: number) => void;
  maxAmount: number;
  disabled?: boolean;
  showAllIn?: boolean;
  onAutoplay?: () => void;
  autoplayActive?: boolean;
  className?: string;
}

const openCustomize = (currency: CurrencyCode) =>
  NiceModal.show(MODALS.EDIT_QUICK_BUY, { currency });

function BetPanelComponent({
  currency,
  onCurrencyChange,
  onSelectAmount,
  maxAmount,
  disabled = false,
  showAllIn = true,
  onAutoplay,
  autoplayActive = false,
  className,
}: BetPanelProps) {
  const presets = useAppSelector(selectQuickBuyPresets);
  const currencyPresets = presets[currency];
  const canCustomize = currency !== 'GIFT';
  const showQuickBuyButtons = currency !== 'GIFT';

  return (
    <div className={`bet-panel${className ? ` ${className}` : ''}`}>
      <div className="bet-panel__toolbar">
        <AssetTabs
          items={CURRENCY_TABS}
          activeId={currency}
          onSelect={onCurrencyChange}
          disabled={disabled}
          iconOnly
        />

        <div className="bet-panel__actions">
          {canCustomize && (
            <button
              type="button"
              className="bet-panel__action-btn"
              onClick={() => openCustomize(currency)}
              aria-label="Customize quick buy amounts"
              disabled={disabled}>
              <Pencil className="bet-panel__action-icon" />
            </button>
          )}

          {onAutoplay && (
            <button
              type="button"
              className={`bet-panel__action-btn${autoplayActive ? ' bet-panel__action-btn--active' : ''}`}
              onClick={onAutoplay}
              aria-label="Autoplay"
              aria-pressed={autoplayActive}
              disabled={disabled}>
              <Zap className="bet-panel__action-icon" />
            </button>
          )}
        </div>
      </div>

      {showQuickBuyButtons && (
        <div className="bet-panel__quick-row">
          {currencyPresets.map((amount, index) => (
            <button
              key={`${amount}-${index}`}
              type="button"
              className="bet-panel__quick-btn"
              onClick={() => onSelectAmount(amount)}
              onContextMenu={(e) => {
                if (!canCustomize) return;
                e.preventDefault();
                openCustomize(currency);
              }}
              disabled={disabled}>
              {formatCurrencyAmount(amount, currency)}
            </button>
          ))}

          {showAllIn && (
            <button
              type="button"
              className="bet-panel__quick-btn bet-panel__quick-btn--all-in"
              onClick={() => onSelectAmount(maxAmount)}
              disabled={disabled || maxAmount <= 0}>
              All In
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const BetPanel = memo(BetPanelComponent);
